<template>

  <!--
    سوییچر لیگ‌ها — یک طرح واحد برای دسکتاپ و موبایل:
    · اسکرول بومی مرورگر (overflow-x) → لمسی واقعی روی موبایل با اینرسی سیستمی
    · scroll-snap + وسط‌چین‌شدن کارت لیگِ فعال (مثل اسلایدر تاریخ صفحهٔ مسابقات)
    · فلش چپ/راست برای پیمایش گام‌به‌گام (روی موبایل هم هست)
    · بدون Swiper — یک وابستگی JS کمتر و بدون مشکل لمس
  -->

  <section class="competition-switcher">

    <!-- فلش راست: نوار به سمت راست می‌لغزد (کارت‌های بعدی) -->
    <button
        type="button"
        class="sw-arrow"
        :disabled="atEnd"
        aria-label="لیگ‌های بعدی"
        @click="step(1)"
    >‹</button>

    <div
        class="sw-scroller"
        ref="scroller"
        dir="rtl"
        @scroll.passive="onScroll"
    >
      <router-link
          v-for="league in leagues"
          :key="league.id"
          :to="`/competition/${league.id}`"
          class="league-card"
          :class="{ active: isActive(league) }"
      >
        <span v-if="league.isNationalTeam" class="nat-badge">ملی</span>
        <img
            :src="league.logo"
            :alt="league.name"
            loading="lazy"
        >
        <span class="card-name">{{ getLeagueName(league) }}</span>
      </router-link>

      <div v-if="!leagues.length" class="sw-placeholder"></div>
    </div>

    <!-- فلش چپ: نوار به سمت چپ می‌لغزد (کارت‌های قبلی) -->
    <button
        type="button"
        class="sw-arrow"
        :disabled="atStart"
        aria-label="لیگ‌های قبلی"
        @click="step(-1)"
    >›</button>

  </section>

</template>

<script>
import { leagueNames } from '@/utils/translations';

export default {

  name: 'CompetitionSwitcher',

  data() {
    return {
      atStart: true,
      atEnd: false,
    };
  },

  computed: {
    leagues() {
      // ترتیب canonical از بک‌اند: ایران اول → لیگ‌های باشگاهی → رقابت‌های تیم‌ملی (با نشان «ملی»)
      return this.$store.getters.competitions || [];
    },
    activeIndex() {
      const id = Number(this.$route.params.id);
      const idx = this.leagues.findIndex(l => Number(l.id) === id);
      return idx === -1 ? 0 : idx;
    },
  },

  watch: {
    // با تغییر لیگِ فعال (ناوبری بین صفحات لیگ) کارت جدید وسط بیاید
    '$route.params.id'() {
      this.$nextTick(() => this.centerCard(this.activeIndex, 'smooth'));
    },
    // لیگ‌ها از استور دیر می‌رسند — بعد از رندر، وسط‌چین کن
    leagues() {
      this.$nextTick(() => {
        this.centerCard(this.activeIndex, 'auto');
        this.onScroll();
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.centerCard(this.activeIndex, 'auto');
      this.onScroll();
    });
    // بعد از فونت‌لود/تصاویر ابعاد عوض می‌شود — یک بار دیگر تنظیم شود
    if (window.addEventListener) {
      this._onLoad = () => {
        this.centerCard(this.activeIndex, 'auto');
        this.onScroll();
      };
      window.addEventListener('load', this._onLoad, { once: true });
    }
  },

  beforeDestroy() {
    if (this._onLoad && window.removeEventListener) {
      window.removeEventListener('load', this._onLoad);
    }
  },

  methods: {

    isActive(league) {
      return Number(this.$route.params.id) === Number(league.id);
    },

    // اول نگاشت شورت‌نیم، بعد نام فارسی دیتابیس (رقابت‌های ملی فارسی seed شده‌اند)
    getLeagueName(league) {
      return leagueNames[league.shortName] || league.name || league.shortName;
    },

    /** کارت i را وسط نوار بیاور — scrollIntoView خودش RTL را درست مدیریت می‌کند */
    centerCard(i, behavior = 'smooth') {
      const el = this.$refs.scroller;
      if (!el) return;
      const cards = el.querySelectorAll('.league-card');
      const card = cards[i];
      if (!card) return;
      try {
        card.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      } catch (e) {
        // مرورگرهای خیلی قدیمی — همان پرش ساده
        card.scrollIntoView(behavior === 'smooth');
      }
    },

    /** فلش‌ها: از نزدیک‌ترین کارت به مرکز، یکی جلو/عقب */
    step(dir) {
      const el = this.$refs.scroller;
      if (!el) return;
      const cards = Array.from(el.querySelectorAll('.league-card'));
      if (!cards.length) return;
      const rect = el.getBoundingClientRect();
      const mid = rect.left + rect.width / 2;
      let nearest = 0, nd = Infinity;
      cards.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - mid);
        if (d < nd) { nd = d; nearest = i; }
      });
      const target = Math.min(cards.length - 1, Math.max(0, nearest + dir));
      this.centerCard(target, 'smooth');
    },

    /** وضعیت لبه‌ها برای غیرفعال‌شدن فلش‌ها — |scrollLeft| در RTL هم همین بازه را دارد */
    onScroll() {
      const el = this.$refs.scroller;
      if (!el) return;
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      const pos = Math.abs(el.scrollLeft);
      this.atStart = pos <= 2;
      this.atEnd = pos >= max - 2;
    },

  },

};

</script>

<style scoped>

/* ============ چیدمان کلی: [فلش] [نوار اسکرولی] [فلش] ============ */
.competition-switcher {
  display: flex;
  align-items: center;
  gap: 8px;

  width: 100%;
  margin-bottom: 22px;
}

.sw-arrow {
  flex-shrink: 0;

  width: 34px;
  height: 72px;

  border: none;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .08);

  color: #024832;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  transition: .2s;
}

.sw-arrow:hover:not(:disabled) {
  background: #eef9f3;
  transform: scale(1.05);
}

.sw-arrow:disabled {
  opacity: .3;
  cursor: default;
  box-shadow: none;
}

/* ============ نوار اسکرولی — لمس بومی + snap ============ */
.sw-scroller {
  flex: 1;
  min-width: 0;

  display: flex;
  gap: 10px;

  overflow-x: auto;
  overflow-y: hidden;

  scroll-snap-type: x proximity;

  padding: 6px 2px 14px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.sw-scroller::-webkit-scrollbar {
  display: none;
}

.sw-placeholder {
  flex: 0 0 10px;
}

/* ============ کارت لیگ ============ */
.league-card {
  position: relative;

  flex: 0 0 120px;
  width: 120px;
  min-width: 120px;
  scroll-snap-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 12px;

  text-decoration: none;
  color: #1f2937;
  background: #fff;

  border-radius: 18px;
  border: 2px solid transparent;

  box-shadow: 0 4px 14px rgba(0, 0, 0, .08);
  transition: transform .2s, border-color .2s, background .2s;
}

.league-card:hover {
  transform: translateY(-3px);
}

.league-card.active {
  border-color: #024832;
  background: #eef9f3;
}

.league-card.active .card-name {
  color: #024832;
}

.league-card img {
  width: 42px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
}

.card-name {
  width: 100%;
  text-align: center;
  white-space: normal;

  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  min-height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;
}

/* نشان «ملی» روی رقابت‌های تیم‌های ملی */
.nat-badge {
  position: absolute;
  top: 6px;
  inset-inline-start: 6px;

  font-size: 8.5px;
  font-weight: 800;
  line-height: 1;

  padding: 3px 5px;
  border-radius: 6px;

  background: #024832;
  color: #42d392;
}

/* ============ موبایل ============ */
@media (max-width: 768px) {
  .competition-switcher {
    gap: 6px;
    margin-bottom: 16px;
  }

  .sw-arrow {
    width: 28px;
    height: 62px;
    font-size: 18px;
    border-radius: 11px;
  }

  .sw-scroller {
    gap: 8px;
    padding: 5px 2px 12px;
  }

  .league-card {
    flex: 0 0 106px;
    width: 106px;
    min-width: 106px;
    gap: 7px;
    padding: 11px 6px 10px;
    border-radius: 13px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
  }

  .league-card:hover {
    transform: none;
  }

  .league-card img {
    width: 32px;
    height: 32px;
  }

  .card-name {
    font-size: 10.5px;
    line-height: 1.35;
    min-height: 30px;
  }
}

/* موبایل کوچک */
@media (max-width: 400px) {
  .league-card {
    flex: 0 0 98px;
    width: 98px;
    min-width: 98px;
    padding: 10px 5px 9px;
    gap: 6px;
  }

  .league-card img {
    width: 28px;
    height: 28px;
  }

  .card-name {
    font-size: 9.8px;
  }
}

</style>
